import { useCallback, useMemo, useState } from 'react';

type TableRow = {
  id: string;
};

export function useTableBulkActions({
  visibleRows,
}: {
  visibleRows: TableRow[];
}) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const areAnySelected = useMemo(() => !!selectedIds.length, [selectedIds]);
  const areAnyVisibleSelected = useMemo(
    () =>
      !!visibleRows.length &&
      visibleRows.some((r) => selectedIds.includes(r.id)),
    [selectedIds, visibleRows],
  );
  const areAllVisibleSelected = useMemo(
    () =>
      !!visibleRows.length &&
      visibleRows.every((r) => selectedIds.includes(r.id)),
    [selectedIds, visibleRows],
  );
  const getIsRowSelected = useCallback(
    (row: TableRow) => selectedIds.includes(row.id),
    [selectedIds],
  );
  const onToggleRow = useCallback(
    (row: TableRow, isChecked: boolean) => {
      if (isChecked) {
        setSelectedIds([...new Set(selectedIds).add(row.id)]);
      } else {
        setSelectedIds(selectedIds.filter((id) => id !== row.id));
      }
    },
    [selectedIds],
  );
  const onToggleAllVisibleRows = useCallback(
    (isChecked: boolean) => {
      const visibleRowIds = visibleRows.map(({ id }) => id);
      if (isChecked) {
        setSelectedIds([...new Set([...selectedIds, ...visibleRowIds])]);
      } else {
        setSelectedIds(selectedIds.filter((id) => !visibleRowIds.includes(id)));
      }
    },
    [selectedIds, visibleRows],
  );

  return {
    areAllVisibleSelected,
    areAnySelected,
    areAnyVisibleSelected,
    getIsRowSelected,
    onToggleAllVisibleRows,
    onToggleRow,
    selectedIds,
    setSelectedIds,
  };
}
